/**
 * Base.js
 *
 * Phase 5 (June 2026) — Player Home Base management page (/base).
 *
 * Shows the player's single home base: where it is, what it auto-mines, the ore
 * that's accrued since the last collection (continuous, uncapped), and the two
 * upgrade tracks (Extractor Rate, Drill Rigs). Lets the player Collect, Upgrade
 * and Abandon. Claiming happens from the System view (must scan a body first).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Home, Gauge, Drill, PackageOpen, ArrowUpCircle, Trash2, Telescope, Coins, Loader2,
  Users, Sprout, FlaskConical, ShieldCheck, Building2, Banknote,
} from 'lucide-react';

const MODULE_ICONS = { housing: Building2, farm: Sprout, lab: FlaskConical, defense: ShieldCheck };

const RARITY_COLOR = {
  Common: 'text-slate-300', Uncommon: 'text-emerald-300', Rare: 'text-cyan-300',
  'Ultra Rare': 'text-violet-300', Legendary: 'text-amber-300', Mythic: 'text-rose-300',
};

function Base() {
  const { api, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  // Phase B — colony progression state
  const [colony, setColony] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/base');
      setData(d);
      if (d?.has_base) {
        const { data: c } = await api.get('/api/colony');
        setColony(c?.colony || null);
      } else {
        setColony(null);
      }
    } catch (_e) {
      setData({ has_base: false });
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const collect = async () => {
    setBusy('collect');
    try {
      const { data: r } = await api.post('/api/base/collect');
      if (r.summary?.length) toast.success(`Collected · ${r.summary.join(' · ')}`);
      else toast.info('Nothing has accrued yet — check back later.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Collection failed'); }
    finally { setBusy(''); }
  };

  const upgrade = async (track) => {
    setBusy(`up-${track}`);
    try {
      const { data: r } = await api.post('/api/base/upgrade', { track });
      if (r.credits != null) updateCredits?.(r.credits);
      if (r.collected?.length) toast.success(`Upgraded · banked ${r.collected.length} ore type(s)`);
      else toast.success('Upgrade installed');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upgrade failed'); }
    finally { setBusy(''); }
  };

  const abandon = async () => {
    if (!window.confirm('Abandon your home base? Accrued ore will be banked, but the base, colony and its upgrades are lost.')) return;
    setBusy('abandon');
    try {
      await api.post('/api/base/abandon');
      toast.success('Home base released.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to abandon'); }
    finally { setBusy(''); }
  };

  // Phase B — colony actions
  const collectIncome = async () => {
    setBusy('colony-collect');
    try {
      const { data: r } = await api.post('/api/colony/collect');
      if (r.credits != null) updateCredits?.(r.credits);
      if (r.gained > 0) toast.success(`Colony income collected · +${r.gained.toLocaleString()} cr`);
      else toast.info('No income accrued yet — your colonists are settling in.');
      if (r.raids > 0 && r.stolen > 0) {
        toast.warning(`Pirates raided ${r.raids}× while you were away · −${Math.round(r.stolen).toLocaleString()} cr lost. Build the Defense Grid!`);
      }
      setColony(r.colony);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Collection failed'); }
    finally { setBusy(''); }
  };

  const buildModule = async (moduleId) => {
    setBusy(`mod-${moduleId}`);
    try {
      const { data: r } = await api.post('/api/colony/build', { module: moduleId });
      if (r.credits != null) updateCredits?.(r.credits);
      toast.success(`${(r.colony?.modules || []).find((m) => m.id === moduleId)?.name || 'Module'} upgraded`);
      if (r.banked_income > 0) toast.info(`Banked +${r.banked_income.toLocaleString()} cr of accrued income`);
      setColony(r.colony);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Build failed'); }
    finally { setBusy(''); }
  };

  const base = data?.base;

  return (
    <div className="min-h-screen">
      <Header breadcrumbs={[{ label: 'Home Base' }]} />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <Home className="w-7 h-7 text-cyan-300" />
          <div>
            <h1 className="text-3xl font-bold text-primary" data-testid="base-title">Home Base</h1>
            <p className="text-sm text-muted-foreground">Your outpost auto-mines ore around the clock.</p>
          </div>
        </div>

        {data === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-cyan-400/60" /></div>
        ) : !data.has_base ? (
          <div className="text-center py-16 border border-cyan-400/15 rounded-xl bg-white/[0.02]" data-testid="base-empty">
            <Telescope className="w-12 h-12 mx-auto text-cyan-400/40 mb-4" />
            <p className="text-lg text-slate-200">You haven&apos;t claimed a home base yet.</p>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Travel to any planet or moon, <span className="text-cyan-300">scan it</span>, then use
              <span className="text-cyan-300"> “Claim Home Base”</span> from the system view.
              Claiming costs <span className="text-amber-300">{(data.claim_cost || 50000).toLocaleString()} cr</span>.
            </p>
            <Button className="mt-6" onClick={() => navigate('/explore/octants')} data-testid="base-go-explore">
              <Telescope className="w-4 h-4 mr-2" /> Go Explore
            </Button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="base-detail">
            {/* Location + output */}
            <div className="rounded-xl border border-cyan-400/20 bg-gradient-to-br from-cyan-950/30 to-transparent p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400/70">Outpost Location</div>
                  <div className="text-xl font-bold text-cyan-100" data-testid="base-body-name">{base.body_name}</div>
                  <div className="text-xs text-slate-400">{base.body_type} · {base.coords_key}</div>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => navigate(`/system/${base.coords_key}`)}
                    className="mt-2 border-cyan-400/40 text-cyan-200 hover:text-cyan-100"
                    data-testid="base-enter-system-btn"
                  >
                    <Telescope className="w-4 h-4 mr-1.5" /> Enter System
                  </Button>
                </div>
                <div className="text-right">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-slate-400">Total Output</div>
                  <div className="text-2xl font-bold text-emerald-300" data-testid="base-total-rate">{base.total_per_hour}/hr</div>
                </div>
              </div>
            </div>

            {/* Accrued / collect */}
            <div className="rounded-xl border border-amber-400/20 bg-amber-500/5 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-200">
                  <PackageOpen className="w-4 h-4" /> Accrued Ore
                </div>
                <span className="text-xs text-slate-400">{(base.hours_since_collect || 0).toFixed(1)}h since last collect</span>
              </div>
              {base.pending?.length ? (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="base-pending">
                  {base.pending.map((p) => (
                    <span key={p.symbol} className="rounded-lg border border-amber-400/25 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-100">
                      {p.qty}u {p.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 mb-4">Nothing accrued yet — your drills are warming up.</p>
              )}
              <Button
                onClick={collect}
                disabled={busy === 'collect' || !base.pending?.length}
                data-testid="base-collect-btn"
                className="bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold"
              >
                {busy === 'collect' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageOpen className="w-4 h-4 mr-2" />}
                Collect Ore
              </Button>
            </div>

            {/* What it's mining */}
            <div className="rounded-xl border border-slate-600/20 bg-white/[0.02] p-5">
              <div className="text-sm font-semibold text-slate-200 mb-3">Active Drills</div>
              <div className="space-y-2">
                {base.production.map((p) => (
                  <div key={p.symbol} className="flex items-center justify-between text-sm" data-testid={`base-prod-${p.symbol}`}>
                    <span className={RARITY_COLOR[p.rarity] || 'text-slate-300'}>{p.name} <span className="text-slate-500 text-xs">({p.rarity})</span></span>
                    <span className="text-emerald-300 font-mono">{p.per_hour}/hr</span>
                  </div>
                ))}
                {base.all_elements?.length > base.production.length && (
                  <p className="text-xs text-slate-500 pt-1">
                    +{base.all_elements.length - base.production.length} more element(s) unlock with additional Drill Rigs.
                  </p>
                )}
              </div>
            </div>

            {/* Phase B — Colony */}
            {colony && (
              <div className="rounded-xl border border-violet-400/25 bg-gradient-to-br from-violet-950/30 to-transparent p-5" data-testid="colony-panel">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-300" />
                    <span className="text-sm font-semibold text-violet-100">Colony</span>
                    <span
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-violet-400/40 text-violet-200"
                      data-testid="colony-level"
                    >
                      Level {colony.level}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-slate-400">Income</div>
                    <div className="text-lg font-bold text-emerald-300" data-testid="colony-income-rate">
                      {colony.income_per_hour.toLocaleString()} cr/hr
                    </div>
                  </div>
                </div>

                {/* Population bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                    <span>Population · +{colony.growth_per_hour}/hr</span>
                    <span className="font-mono" data-testid="colony-population">
                      {Math.floor(colony.population)} / {colony.capacity}
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-900/80 border border-violet-400/20 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-600 to-violet-300 transition-all"
                      style={{ width: `${Math.min(100, (colony.population / colony.capacity) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Pending income + raids */}
                <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/5 p-3 mb-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      <span className="text-slate-400">Accrued: </span>
                      <span className="text-emerald-300 font-mono font-semibold" data-testid="colony-pending">
                        {Math.floor(colony.pending?.net || 0).toLocaleString()} cr
                      </span>
                      {colony.pending?.raids > 0 && colony.pending?.stolen > 0 && (
                        <span className="text-rose-400 text-xs ml-2">
                          (pirates stole {Math.round(colony.pending.stolen).toLocaleString()} cr in {colony.pending.raids} raid{colony.pending.raids > 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={collectIncome}
                      disabled={busy === 'colony-collect' || (colony.pending?.net || 0) < 1}
                      size="sm"
                      data-testid="colony-collect-btn"
                      className="bg-emerald-500/90 hover:bg-emerald-400 text-slate-900 font-semibold"
                    >
                      {busy === 'colony-collect' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
                      Collect Income
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Pirates raid every {colony.raid_window_hours}h, stealing {colony.raid_loss_pct}% of uncollected income.
                  </p>
                </div>

                {/* Module grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(colony.modules || []).map((m) => {
                    const Icon = MODULE_ICONS[m.id] || Building2;
                    const maxed = m.level >= m.max_level || m.next_cost == null;
                    return (
                      <div key={m.id} className="rounded-lg border border-violet-400/15 bg-white/[0.02] p-3" data-testid={`colony-module-${m.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-violet-300" />
                          <span className="text-sm font-semibold text-violet-100">{m.name}</span>
                          <span className="ml-auto text-xs text-slate-400">Lv {m.level}/{m.max_level}</span>
                        </div>
                        <p className="text-xs text-slate-400 mb-2">{m.desc}</p>
                        <Button
                          onClick={() => buildModule(m.id)}
                          disabled={busy === `mod-${m.id}` || maxed}
                          size="sm"
                          className="w-full bg-violet-600/80 hover:bg-violet-500 text-white font-semibold"
                          data-testid={`colony-build-${m.id}-btn`}
                        >
                          {busy === `mod-${m.id}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : maxed ? null : <ArrowUpCircle className="w-4 h-4 mr-2" />}
                          {maxed ? 'Max Level' : (
                            <span className="flex items-center gap-1.5">
                              Build <Coins className="w-3.5 h-3.5" /> {Number(m.next_cost).toLocaleString()}
                            </span>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upgrades */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UpgradeCard
                icon={Gauge} title="Extractor Rate" level={base.rate_level} max={base.max_level}
                desc="+50% total ore/hour per level"
                cost={base.rate_upgrade_cost} busy={busy === 'up-rate'}
                onUpgrade={() => upgrade('rate')} testid="base-upgrade-rate"
              />
              <UpgradeCard
                icon={Drill} title="Drill Rigs" level={base.drill_level} max={base.max_level}
                desc="Mine one more element type per level"
                cost={base.drill_upgrade_cost} busy={busy === 'up-drill'}
                onUpgrade={() => upgrade('drill')} testid="base-upgrade-drill"
              />
            </div>

            <button
              onClick={abandon}
              disabled={busy === 'abandon'}
              className="text-xs text-rose-400/70 hover:text-rose-300 transition-colors flex items-center gap-1.5 pt-2"
              data-testid="base-abandon-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Abandon this base
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function UpgradeCard({ icon: Icon, title, level, max, desc, cost, busy, onUpgrade, testid }) {
  const maxed = level >= max || cost == null;
  return (
    <div className="rounded-xl border border-cyan-400/15 bg-white/[0.02] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-cyan-300" />
        <span className="text-sm font-semibold text-cyan-100">{title}</span>
        <span className="ml-auto text-xs text-slate-400">Lv {level}/{max}</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{desc}</p>
      <Button
        onClick={onUpgrade} disabled={busy || maxed}
        className="w-full bg-cyan-600/80 hover:bg-cyan-500 text-white font-semibold"
        data-testid={`${testid}-btn`}
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          : maxed ? null
          : <ArrowUpCircle className="w-4 h-4 mr-2" />}
        {maxed ? 'Max Level' : (
          <span className="flex items-center gap-1.5">
            Upgrade <Coins className="w-3.5 h-3.5" /> {Number(cost).toLocaleString()}
          </span>
        )}
      </Button>
    </div>
  );
}

export default Base;
